import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { writeContract, waitForTransactionReceipt } from '@wagmi/core';
import { isAddress } from 'viem';

import incentivesContractABI from '../../contracts/tgetherIncentives.json';
import * as config from '../../config.tsx';
const incentivesContractAddress = process.env.REACT_APP_INCENTIVES_CONTRACT_ADDRESS as `0x${string}`;

function SetIncentiveModal({ show, setShow, communityName }: { show: boolean, setShow: (show: boolean) => void, communityName: string }) {
    const [formData, setFormData] = useState({
        structure: '0', // Default to "Equal"
        communityFee: '',
        contractRecieveAddress: '',
    });
    const [transactionPending, setTransactionPending] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: type === 'number' ? Number(value) : value,
        }));
    };
    
    

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const communityFeeValue = Number(formData.communityFee);
        if (communityFeeValue < 0 || communityFeeValue > 100) {
            setErrorMessage('Community fee must be between 0 and 100.');
            return;
        }

        if (formData.contractRecieveAddress && !isAddress(formData.contractRecieveAddress)) {
            setErrorMessage('Please enter a valid Ethereum address or leave the field blank.');
            return;
        }

        // Clear error message if validation passes
        setErrorMessage('');
        try {
            setTransactionPending(true);

            // Write the contract using the writeContract function
            const tx = await writeContract(config.config, {
                address: incentivesContractAddress,
                abi: incentivesContractABI.abi,
                functionName: 'setParams',
                args: [
                    communityName,
                    parseInt(formData.structure),
                    Number(formData.communityFee),
                    formData.contractRecieveAddress,
                ],
            });

            // Wait for the transaction receipt
            await waitForTransactionReceipt(config.config, {
                hash: tx,
            });

            setShowSuccessAlert(true);
        } catch (error) {
            console.error('Error setting incentive parameters:', error);
        } finally {
            setTransactionPending(false);
        }
    };

    const handleClose = () => {
        setShow(false);
        setFormData({ structure: '0', communityFee: '', contractRecieveAddress: '' });
        setTransactionPending(false);
        setShowSuccessAlert(false);
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Set Incentive Parameters</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {showSuccessAlert && <Alert variant="success">Incentive parameters successfully set!</Alert>}
                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

                <Form onSubmit={handleSubmit}>
                    <Form.Group controlId="structure">
                        <Form.Label>Incentive Structure</Form.Label>
                        <Form.Control
                            as="select"
                            name="structure"
                            value={formData.structure}
                            onChange={handleInputChange}
                        >
                            <option value="0">Equal</option>
                            <option value="1">Consensus Aligned</option>
                        </Form.Control>
                    </Form.Group>
                    <Form.Group controlId="communityFee">
                        <Form.Label>Community Fee (%)</Form.Label>
                        <Form.Control
                            type="number"
                            name="communityFee"
                            value={formData.communityFee}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                    <Form.Group controlId="contractRecieveAddress">
                        <Form.Label>Contract Receive Address</Form.Label>
                        <Form.Control
                            type="text"
                            name="contractRecieveAddress"
                            value={formData.contractRecieveAddress}
                            onChange={handleInputChange}
                            required
                        />
                    </Form.Group>
                    <Button variant="primary" type="submit" disabled={transactionPending}>
                        Set Parameters
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

export default SetIncentiveModal;
